import AutorenewIcon from "@mui/icons-material/Autorenew";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import EventNoteIcon from "@mui/icons-material/EventNote";
import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useQueryClient } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import type { CSSProperties } from "react";
import { type FC, Fragment, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Popover as TinyPopover } from "react-tiny-popover";
import { CallInIndicator } from "../../../../../../components/CallInIndicator/CallInIndicator";
import ActualShiftJobHintDialog from "../../../../../../components/EditJobPopover/ActualShiftJobHintDialog";
import { EditJobPopover } from "../../../../../../components/EditJobPopover/EditJobPopover";
import { InHouseTooltip } from "../../../../../../components/InHouseTooltip/InHouseTooltip";
import { NormalizedNurseMenu } from "../../../../../../components/NormalizedNurseMenu/NormalizedNurseMenu";
import { RawPunchesChip } from "../../../../../../components/UIToolkit/Popups/RawPunchesDialog/RawPunchesChip";
import { ThemedSvg } from "../../../../../../components/UIToolkit/ThemedSvg";
import { useAnalytics } from "../../../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../../../contexts/HospitalDataProvider";
import { invalidatePbjPredictionJobCoverage } from "../../../../../../pages/PbjModel/hooks/pbjQueryOptions";
import { backendService } from "../../../../../../services/backend.service";
import { INK, INK_BLACK, NEUTRAL, STATUS, SURFACE } from "../../../../../../theme/tokens";
import { getActualJobAffordance } from "../../../../../../utils/actualJobAffordance";
import { formatHoursMinutes, formatPtoTimeRange, isFullStaffingDayPto } from "../../../../../../utils/dateUtils";
import {
	type ActualNurse,
	type BaseNurseShift,
	type CalledOffShift,
	ChangesSource,
	type DayOff,
	type Nurse,
	type NurseInShift,
	type SpecificPreference,
} from "../../../../../../utils/models";
import { applyShiftJobLocal } from "../../../../../../utils/shiftJobUpdate";
import { ClickedCellEditor } from "./ClickedCellEditor";
import { useDayCellState } from "./useDayCellState";

type Props = {
	currentDate: DateTime;
	nurseInShiftArray?: NurseInShift[];
	actualNurseInShiftArray?: ActualNurse[];
	cellIndex: number;
	showDatesDebug?: boolean;
	viewOnly: boolean;
	disabled: boolean;
	tooltipTitle?: string;
	nurseFromProfile?: Nurse;
	onCreatedSingleShift?: () => void;
	dayPreference?: SpecificPreference;
	dayOff?: DayOff;
	callIns: CalledOffShift[];
	isToday?: boolean;
};

const shiftTimeLabel = (shift: BaseNurseShift) => `${shift.startTime.toFormat("h:mm a")} - ${shift.endTime.toFormat("h:mm a")}`;

// Module-level static styles — hoisted so emotion can cache the serialized CSS across renders.

const popoverContentStyle: CSSProperties = {
	backgroundColor: SURFACE,
	width: "fit-content",
	height: "fit-content",
	boxShadow: `0 0 8px ${alpha(INK_BLACK, 0.15)}`,
	borderRadius: "8px",
};

const dateLabelWrapperStyle: CSSProperties = {
	position: "absolute",
	left: 4,
	top: 4,
};

// Mirrors the date label across the cell so the two corner annotations balance. `zIndex` keeps the
// marker above shift cards, which start below it but grow upward once a day holds several.
const callInMarkerWrapperSx: SxProps<Theme> = {
	position: "absolute",
	right: 4,
	top: 4,
	zIndex: 1,
	display: "flex",
	cursor: "default",
};

const baseLabelTypographyStyle: CSSProperties = {
	fontWeight: 500,
	fontStyle: "Medium",
	fontSize: "12px",
	lineHeight: "100%",
	letterSpacing: "0%",
	color: alpha(INK.SECONDARY, 0.85),
};

const editableShiftTimeStyle: CSSProperties = {
	fontWeight: 500,
	fontStyle: "Medium",
	fontSize: "13px",
	lineHeight: "100%",
	letterSpacing: "0%",
	color: alpha(INK.SECONDARY, 0.85),
};

const editableShiftSubStyle: CSSProperties = {
	fontWeight: 500,
	fontStyle: "Medium",
	fontSize: "12px",
	lineHeight: "100%",
	letterSpacing: "0%",
	color: alpha(INK.SECONDARY, 0.7),
};

const preferenceLabelStyle: CSSProperties = {
	fontWeight: 500,
	fontStyle: "Medium",
	fontSize: "13px",
	lineHeight: "100%",
	letterSpacing: "0%",
	color: STATUS.SUCCESS.main,
};

const preferenceTimeStyle: CSSProperties = {
	fontWeight: 500,
	fontStyle: "Medium",
	fontSize: "12px",
	lineHeight: "100%",
	letterSpacing: "0%",
	color: alpha(INK.SECONDARY, 0.7),
	marginTop: "4px",
};

const recurringIconStyle: CSSProperties = { color: alpha(INK.SECONDARY, 0.5), fontSize: "18px" };

const dayOffWrapperSx: SxProps<Theme> = {
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	gap: 0.5,
	cursor: "default",
};

const preferenceCardSx: SxProps<Theme> = {
	width: "100%",
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	justifyContent: "center",
	border: `1px solid ${NEUTRAL[200]}`,
	borderRadius: "8px",
	backgroundColor: STATUS.SUCCESS.light,
	padding: "4px 8px",
};

const shiftListWrapperSx: SxProps<Theme> = {
	display: "flex",
	flexDirection: "column",
	gap: 0.5,
	width: "100%",
	mt: 1.5,
};

const actualShiftCardSx: SxProps<Theme> = {
	width: "100%",
	borderLeft: `3px solid ${STATUS.SUCCESS.main}`,
	borderRadius: "0 6px 6px 0",
	backgroundColor: STATUS.SUCCESS.light,
	padding: "4px 6px",
};

const shiftRowSx: SxProps<Theme> = { display: "flex", alignItems: "center", gap: 0.5 };

const actualShiftIconSx: SxProps<Theme> = { fontSize: 11, color: STATUS.SUCCESS.main, flexShrink: 0 };

const actualShiftTimeSx: SxProps<Theme> = { fontSize: "11px", fontWeight: 600, color: STATUS.SUCCESS.main, lineHeight: 1.2 };

// Scheduled shift styles are theme-aware (primary color follows the user's theme),
// so they're defined as sx callbacks rather than static module-level constants.
const scheduledShiftCardSx: SxProps<Theme> = (theme) => ({
	width: "100%",
	borderLeft: `3px solid ${theme.palette.primary.main}`,
	borderRadius: "0 6px 6px 0",
	backgroundColor: alpha(theme.palette.primary.main, 0.12),
	padding: "4px 6px",
});
const scheduledShiftIconSx: SxProps<Theme> = (theme) => ({ fontSize: 11, color: theme.palette.primary.main, flexShrink: 0 });
const scheduledShiftTimeSx: SxProps<Theme> = (theme) => ({ fontSize: "11px", fontWeight: 600, color: theme.palette.primary.dark, lineHeight: 1.2 });

const shiftLocationSx: SxProps<Theme> = { fontSize: "10px", color: alpha(INK.SECONDARY, 0.55), pl: "15px", lineHeight: 1.2 };
const shiftJobNameSx: SxProps<Theme> = { fontSize: "10px", color: alpha(INK.SECONDARY, 0.7), pl: "15px", lineHeight: 1.2, fontStyle: "italic" };

const editableShiftRowSx: SxProps<Theme> = { display: "flex", alignItems: "center", gap: 1 };

// Outer cell sx — has 4 dynamic fields (backgroundColor, gridColumn, border, borderRadius, cursor, opacity, :hover).
// We hoist the static base + compose a small overlay inline.
const cellBaseSx = {
	px: 1,
	py: 1,
	gap: 0.75,
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	justifyContent: "center",
	userSelect: "none",
	position: "relative",
	// Stay within the fixed row track: clip overflowing shifts inside the cell
	minHeight: 0,
	overflow: "hidden",
} as const;

export const DayCell: FC<Props> = ({
	cellIndex,
	showDatesDebug,
	nurseInShiftArray,
	actualNurseInShiftArray,
	currentDate,
	viewOnly,
	nurseFromProfile,
	disabled,
	tooltipTitle,
	onCreatedSingleShift,
	dayPreference,
	dayOff,
	callIns,
	isToday,
}) => {
	const { t } = useTranslation();
	const { chosenDepartment, setHospitalData, showErrorSnackbar } = useHospitalData();
	const { track, trackError } = useAnalytics();
	const queryClient = useQueryClient();
	const interactionsDisabled = useMemo(() => viewOnly || disabled, [viewOnly, disabled]);

	const {
		nurseMenuAnchorAndSpecificNurse,
		handleCellClick,
		popoverOpen,
		closeNurseMenu,
		selectedCreationType,
		setSelectedCreationType,
		onPopoverClickOutside,
		handleClickedCellEditorClose,
		editingActualJob,
		setEditingActualJob,
		singleJobHint,
		setSingleJobHint,
	} = useDayCellState({
		onCreatedSingleShift,
		interactionsDisabled,
	});

	// An actual shift's job can be reassigned straight from the profile grid when the employee holds
	// more than one job (DAT-502). A single-job employee has nothing to switch to, so the card opens a
	// dialog explaining how to add a second job first (DAT-615) — the job name itself stays hidden on
	// the card either way, since the dialog names it.
	// Memoized because it returns a fresh object: without this, `openActualJobAffordance` would get a
	// new identity every render and invalidate the `cellData` memo on every pass.
	const actualJobAffordance = useMemo(() => getActualJobAffordance(nurseFromProfile?.jobs), [nurseFromProfile?.jobs]);
	const canEditActualJob = actualJobAffordance.kind === "editable";
	const isActualCardInteractive = actualJobAffordance.kind !== "inert";
	const actualCardTooltip = canEditActualJob
		? t("actual.shift.edit.job.tooltip")
		: actualJobAffordance.kind === "explainable"
			? t("actual.shift.single.job.tooltip")
			: t("actual");

	// One click target, two outcomes: reassign in place for multi-job employees, explain the flow for
	// single-job ones. Inert employees never get the handler attached.
	const openActualJobAffordance = useCallback(
		(shiftId: string, nurseId: number, element: HTMLElement) => {
			if (actualJobAffordance.kind === "editable") {
				setEditingActualJob({ id: shiftId, element });
				return;
			}
			if (actualJobAffordance.kind === "explainable") {
				track("actual_shift_single_job_hint_opened", { shift_id: shiftId, nurse_id: nurseId, source: "profile" });
				setSingleJobHint({ jobName: actualJobAffordance.job.displayName });
			}
		},
		[actualJobAffordance, setEditingActualJob, setSingleJobHint, track],
	);

	const handleActualJobChange = useCallback(
		(shiftId: string, nurseId: number, newJobId: string) => {
			setEditingActualJob(null);
			track("shift_job_update_start", { shift_id: shiftId, nurse_id: nurseId, new_job_id: newJobId });
			void backendService
				.updateShiftJob(shiftId, newJobId, true)
				.then((response) => {
					track("shift_job_update_success", { shift_id: shiftId, nurse_id: nurseId });
					invalidatePbjPredictionJobCoverage(queryClient);
					setHospitalData((prev) => applyShiftJobLocal(prev, { shiftId, nurseId, newJobId, excludeFromPpd: response.data.excludeFromPpd, isActual: true }));
				})
				.catch((error: unknown) => {
					trackError("shift_job_update_error", {
						shift_id: shiftId,
						nurse_id: nurseId,
						error_message: error instanceof Error ? error.message : String(error),
					});
					showErrorSnackbar(t("failed.to.update.shift.job"), { action: "shift job update failed", source: ChangesSource.Profile }, error);
				});
		},
		[queryClient, setEditingActualJob, setHospitalData, showErrorSnackbar, track, trackError, t],
	);

	const popoverContent = useMemo(() => {
		return (
			<Box style={popoverContentStyle}>
				{!interactionsDisabled && popoverOpen ? (
					<ClickedCellEditor
						date={currentDate}
						onCloseCalled={handleClickedCellEditorClose}
						nurseFromProfile={nurseFromProfile}
						selectedCreationType={selectedCreationType}
						setSelectedCreationType={setSelectedCreationType}
					/>
				) : null}
			</Box>
		);
	}, [currentDate, interactionsDisabled, popoverOpen, handleClickedCellEditorClose, nurseFromProfile, selectedCreationType, setSelectedCreationType]);

	const singleFocused = useMemo(() => popoverOpen && !nurseMenuAnchorAndSpecificNurse, [popoverOpen, nurseMenuAnchorAndSpecificNurse]);

	const hasScheduled = !!nurseInShiftArray?.length;
	const hasActual = !!actualNurseInShiftArray?.length;

	const hasInShiftArrayValue = !!nurseInShiftArray;
	const cellSx = useMemo<SxProps<Theme>>(
		() => ({
			...cellBaseSx,
			backgroundColor: hasInShiftArrayValue ? "#fff" : "#F1F3F9",
			gridColumn: `${(cellIndex % 7) + 1}`,
			border: isToday ? "2px solid" : `1px solid ${singleFocused ? "#4D5579" : "#ECEFF1"}`,
			borderColor: isToday ? "primary.main" : undefined,
			borderRadius: singleFocused ? "8px" : "0px",
			cursor: interactionsDisabled ? "not-allowed" : "cell",
			opacity: disabled ? 0.4 : 1,
			":hover": {
				backgroundColor: interactionsDisabled ? undefined : "rgba(0,0,0,0.025)",
			},
		}),
		[hasInShiftArrayValue, cellIndex, singleFocused, interactionsDisabled, disabled, isToday],
	);

	const cellData = useMemo(() => {
		const cellBox = (
			<Box data-testid="profile-schedule-day-cell" key={currentDate.toISO()} onClick={interactionsDisabled ? undefined : handleCellClick} sx={cellSx}>
				<Box style={dateLabelWrapperStyle}>
					<InHouseTooltip title={currentDate.toFormat("MMMM d, yyyy")}>
						<Typography style={baseLabelTypographyStyle}>{currentDate.toFormat("MM/dd")}</Typography>
					</InHouseTooltip>
				</Box>
				{!!callIns?.length && (
					// `stopPropagation` so tapping the marker reads its tooltip instead of opening the
					// cell's shift-creation popover underneath it.
					<Box onClick={(event) => event.stopPropagation()} sx={callInMarkerWrapperSx}>
						<CallInIndicator callIns={callIns} dataTestId={`profile-schedule-call-in-${currentDate.toFormat("yyyy-MM-dd")}`} />
					</Box>
				)}
				{showDatesDebug && currentDate.toJSDate().toLocaleDateString()}
				{dayOff && (
					<Box onClick={(e) => e.stopPropagation()} sx={dayOffWrapperSx}>
						<ThemedSvg src="/day-off.svg" alt="Day off" size={20} color="var(--inhouse-primary)" />
						<Typography style={baseLabelTypographyStyle}>{t("time.off")}</Typography>
						<Typography style={baseLabelTypographyStyle}>
							{/* Exact-times (partial-day) PTO shows its window instead of a bare hours count (DAT-230). */}
							{isFullStaffingDayPto(dayOff.dateFrom, dayOff.dateTo, chosenDepartment.dayStart)
								? formatHoursMinutes(dayOff.hours)
								: formatPtoTimeRange(dayOff.dateFrom, dayOff.dateTo)}
						</Typography>
					</Box>
				)}
				{!hasScheduled && !hasActual && !dayOff ? (
					dayPreference ? (
						<Box sx={preferenceCardSx}>
							<Typography style={preferenceLabelStyle}>Prefers</Typography>
							<Typography style={preferenceTimeStyle}>
								{`${dayPreference.start_date.toFormat("h:mm a")} - ${dayPreference.end_date.toFormat("h:mm a")}`}
							</Typography>
						</Box>
					) : (
						"--"
					)
				) : viewOnly ? (
					<Box sx={shiftListWrapperSx}>
						{(actualNurseInShiftArray ?? []).map((shift) => (
							<Fragment key={shift.id}>
								<InHouseTooltip title={actualCardTooltip}>
									<Box
										sx={
											isActualCardInteractive
												? {
														...actualShiftCardSx,
														// The enclosing cell sets `not-allowed` while interactions are disabled, and actual
														// cards only render in that read-only mode — so the pointer has to be reasserted.
														cursor: "pointer",
														"&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: "2px" },
													}
												: actualShiftCardSx
										}
										role={isActualCardInteractive ? "button" : undefined}
										tabIndex={isActualCardInteractive ? 0 : undefined}
										aria-label={isActualCardInteractive ? actualCardTooltip : undefined}
										onClick={
											isActualCardInteractive
												? (event) => {
														event.stopPropagation();
														openActualJobAffordance(shift.id, shift.nurseId, event.currentTarget);
													}
												: undefined
										}
										onKeyDown={
											isActualCardInteractive
												? (event) => {
														if (event.key !== "Enter" && event.key !== " ") return;
														event.preventDefault();
														event.stopPropagation();
														openActualJobAffordance(shift.id, shift.nurseId, event.currentTarget);
													}
												: undefined
										}
										data-testid={`profile-actual-shift-card-${shift.id}`}
									>
										<Box sx={shiftRowSx}>
											<CheckCircleOutlineIcon sx={actualShiftIconSx} />
											<Typography sx={actualShiftTimeSx}>{shiftTimeLabel(shift)}</Typography>
											{/* DAT-221: support-only raw-punch reveal on the actual shift (renders null for non-support). */}
											<RawPunchesChip shiftId={shift.id} nurseId={shift.nurseId} />
										</Box>
										{shift.location && <Typography sx={shiftLocationSx}>{shift.location}</Typography>}
										{shift.jobName && canEditActualJob && <Typography sx={shiftJobNameSx}>{shift.jobName}</Typography>}
									</Box>
								</InHouseTooltip>
								{editingActualJob?.id === shift.id && (
									<EditJobPopover
										currentJobId={shift.jobId ?? ""}
										availableJobs={nurseFromProfile?.jobs ?? []}
										onJobChange={(newJobId) => handleActualJobChange(shift.id, shift.nurseId, newJobId)}
										onClose={() => setEditingActualJob(null)}
										anchorEl={editingActualJob.element}
									/>
								)}
							</Fragment>
						))}
						{(nurseInShiftArray ?? []).map((shift) => (
							<InHouseTooltip key={shift.id} title={t("scheduled")}>
								<Box sx={scheduledShiftCardSx}>
									<Box sx={shiftRowSx}>
										<EventNoteIcon sx={scheduledShiftIconSx} />
										<Typography noWrap sx={scheduledShiftTimeSx}>
											{shiftTimeLabel(shift)}
										</Typography>
									</Box>
									{shift.location && <Typography sx={shiftLocationSx}>{shift.location}</Typography>}
									{shift.jobName && (nurseFromProfile?.jobs?.length ?? 0) > 1 && <Typography sx={shiftJobNameSx}>{shift.jobName}</Typography>}
								</Box>
							</InHouseTooltip>
						))}
					</Box>
				) : (
					(nurseInShiftArray ?? []).map((currentNurseInShift: NurseInShift) => {
						const isFocused = !!nurseMenuAnchorAndSpecificNurse && nurseMenuAnchorAndSpecificNurse.specificNurseInShift.id === currentNurseInShift.id;
						return (
							<Box
								key={currentNurseInShift.id}
								onClick={disabled ? undefined : (event) => handleCellClick(event, currentNurseInShift)}
								sx={{
									width: "100%",
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									cursor: disabled ? "not-allowed" : "pointer",
									border: isFocused ? `1px solid ${INK.SECONDARY}` : `1px solid ${NEUTRAL[200]}`,
									borderRadius: "8px",
								}}
							>
								<Box sx={editableShiftRowSx}>
									{currentNurseInShift.recurringShiftId && (
										<InHouseTooltip title={"Recurring Shift"}>
											<AutorenewIcon style={recurringIconStyle} />
										</InHouseTooltip>
									)}
									<Typography style={editableShiftTimeStyle}>
										{`${currentNurseInShift.startTime.toFormat("h:mm a")} - ${currentNurseInShift.endTime.toFormat("h:mm a")}`}
									</Typography>
								</Box>
								{currentNurseInShift.location && <Typography style={editableShiftSubStyle}>{currentNurseInShift.location}</Typography>}
								{currentNurseInShift.jobName && ((nurseFromProfile?.jobs?.length ?? 0) > 1 || (nurseFromProfile?.jobIds?.length ?? 0) > 1) && (
									<Typography style={editableShiftSubStyle}>{currentNurseInShift.jobName}</Typography>
								)}
							</Box>
						);
					})
				)}
			</Box>
		);

		if (!tooltipTitle) return cellBox;

		return (
			<InHouseTooltip key={currentDate.toISO()} title={tooltipTitle}>
				{cellBox}
			</InHouseTooltip>
		);
	}, [
		currentDate,
		nurseInShiftArray,
		actualNurseInShiftArray,
		hasScheduled,
		hasActual,
		showDatesDebug,
		tooltipTitle,
		handleCellClick,
		nurseMenuAnchorAndSpecificNurse,
		disabled,
		interactionsDisabled,
		viewOnly,
		dayPreference,
		dayOff,
		callIns,
		t,
		cellSx,
		nurseFromProfile,
		chosenDepartment.dayStart,
		canEditActualJob,
		isActualCardInteractive,
		actualCardTooltip,
		openActualJobAffordance,
		editingActualJob,
		setEditingActualJob,
		handleActualJobChange,
	]);

	return (
		<Fragment>
			<TinyPopover
				key={currentDate.toISO()}
				isOpen={popoverOpen}
				positions={["bottom", "right", "left", "top"]}
				reposition={true}
				align="center"
				onClickOutside={onPopoverClickOutside}
				content={popoverContent}
			>
				{cellData}
			</TinyPopover>

			{!interactionsDisabled && nurseMenuAnchorAndSpecificNurse && (
				<NormalizedNurseMenu
					nurseInShift={nurseMenuAnchorAndSpecificNurse.specificNurseInShift}
					menuAnchorElement={nurseMenuAnchorAndSpecificNurse.element}
					onClose={closeNurseMenu}
					source={ChangesSource.Profile}
					displayProfileButton={false}
					allowEditLocationAndHours={true}
					showActual={false}
				/>
			)}

			{/* Outside `cellData` on purpose: nested in the clickable card, closing it would bubble back
			    through the React tree and re-open it, and it would also need a `useMemo` dependency. No
			    "go to profile" action here — the profile is where we already are. */}
			{!!singleJobHint && <ActualShiftJobHintDialog open={true} jobName={singleJobHint.jobName} onClose={() => setSingleJobHint(null)} />}
		</Fragment>
	);
};
